import { createFileRoute, redirect } from "@tanstack/react-router";
import { SignInScreen } from "@/features/auth/components/SignInScreen";
import { clientConfigQuery, sessionQuery } from "@/features/auth/queries";
import { signInErrorMessage } from "@/features/auth/sign-in-errors";
import { safeNextPath } from "@/lib/redirects";

const optionalString = (value: unknown) => (typeof value === "string" ? value : undefined);

/**
 * Where a guest goes for a page only an account can use: the sign-in screen, worded for creating
 * an account, whose buttons connect a provider to the guest so everything they made stays theirs.
 * Anyone signed out signs in instead, and anyone with an account goes straight on.
 */
export const Route = createFileRoute("/create-account")({
  head: () => ({ meta: [{ title: "Create an account · Pax.co" }] }),
  validateSearch: (search: Record<string, unknown>): { next?: string; error?: string } => ({
    next: optionalString(search.next),
    error: optionalString(search.error),
  }),
  beforeLoad: async ({ context: { queryClient }, search }) => {
    const session = await queryClient.query(sessionQuery);
    const next = safeNextPath(search.next);
    if (!session) {
      throw redirect({ to: "/login", search: { next, requires: "account" }, replace: true });
    }
    if (!session.user.isAnonymous) throw redirect({ href: next, replace: true });
  },
  loader: ({ context: { queryClient } }) => queryClient.query(clientConfigQuery),
  component: CreateAccountPage,
});

function CreateAccountPage() {
  const { next, error } = Route.useSearch();
  const methods = Route.useLoaderData();
  return (
    <SignInScreen
      mode="createAccount"
      methods={methods}
      next={safeNextPath(next)}
      notice={signInErrorMessage(error)}
    />
  );
}
